import React from 'react';
import {useQuery} from '@tanstack/react-query';
import {useParams, useSearchParams} from 'react-router-dom';
import {DetailPageFrame} from '@/components/football';
import WyscoutPlayerMatchContent from '@/components/players/WyscoutPlayerMatchContent';
import {playerMatchMoments} from '@/components/players/playerMatchMoments';
import {useWorkspace} from '@/context/WorkspaceContext';
import {wyscoutFixtureAPI, wyscoutFixtureKeys} from '@/features/players/wyscoutFixtureApi';
import {useWyscoutFixtureRefreshAnnouncement} from '@/features/players/useWyscoutFixtureRefreshAnnouncement';
import WyscoutEventsAnalysis from '@/pages/matches/detail/WyscoutEventsAnalysis';
import {getV2Error} from '@/services/api/v2';

interface WyscoutPlayerMatchPageProps {
  mode?: 'squad' | 'scouting';
}

const safePlayerReturn = (candidate: string | null, root: string, playerId: string): string => {
  const fallback = `${root}/${encodeURIComponent(playerId)}`;
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//') || candidate.includes('\\') || candidate.includes('#')) {
    return fallback;
  }
  const rawPath = candidate.split('?', 1)[0];
  if (rawPath !== fallback) return fallback;
  try {
    const parsed = new URL(candidate, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname === fallback ? candidate : fallback;
  } catch {
    return fallback;
  }
};

const WyscoutPlayerMatchPage: React.FC<WyscoutPlayerMatchPageProps> = ({mode = 'squad'}) => {
  const {id = '', matchId: rawMatchId = ''} = useParams<{id: string; matchId: string}>();
  const [searchParams] = useSearchParams();
  const {currentWorkspace} = useWorkspace();
  const workspaceId = currentWorkspace?.id ?? '';
  const root = mode === 'squad' ? '/players' : '/scouting';
  const validPlayerId = id.length > 0 && id.trim() === id && id !== '.' && id !== '..' && !/[\\/]/.test(id);
  const matchId = /^[1-9]\d*$/.test(rawMatchId) ? Number(rawMatchId) : 0;
  const validMatchId = Number.isSafeInteger(matchId) && matchId > 0;
  const backHref = safePlayerReturn(searchParams.get('from'), root, id);
  const query = useQuery({
    queryKey: wyscoutFixtureKeys.detail(workspaceId, id, matchId),
    queryFn: ({signal}) => wyscoutFixtureAPI.detail(workspaceId, id, matchId, signal),
    enabled: Boolean(workspaceId && validPlayerId && validMatchId),
    retry: false,
  });
  const refreshAnnouncement = useWyscoutFixtureRefreshAnnouncement(
    workspaceId && validPlayerId && validMatchId ? `${workspaceId}:${id}:${matchId}` : null,
    query.fetchStatus,
    query.isError,
    query.dataUpdatedAt,
    query.data,
  );
  const queryError = query.isError ? getV2Error(query.error) : null;
  const accessInvalidated = queryError?.code === 'not_found' || queryError?.code === 'forbidden';

  if (!validPlayerId) return <p role="alert">Invalid player ID.</p>;
  if (!validMatchId) return <p role="alert">Invalid match ID.</p>;
  if (!workspaceId) return <p role="status">Select a workspace to view this match.</p>;
  if (query.isPending) {
    return <div role="status" aria-label="Loading player match" className="space-y-4 animate-pulse motion-reduce:animate-none"><span className="sr-only">Loading player match</span><div className="h-32 rounded-xl bg-gray-100 dark:bg-gray-800" /><div className="h-72 rounded-xl bg-gray-100 dark:bg-gray-800" /></div>;
  }
  if (!query.data || accessInvalidated) {
    const message = queryError?.message || 'Unable to load this match.';
    return <div role="alert" className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-5 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100"><p>{message}</p><button type="button" className="min-h-11 rounded-md border border-red-300 px-4 text-sm font-bold dark:border-red-700" onClick={() => { void query.refetch(); }}>Retry</button></div>;
  }

  const detail = query.data;
  const staleError = queryError?.message ?? null;
  const fixture = detail.fixture;
  const playerIsHome = fixture.playerTeamId === detail.match.homeTeamId;
  const opponentIsHome = fixture.opponentTeamId === detail.match.homeTeamId;
  const homeTeamName = playerIsHome ? fixture.playerTeamName : opponentIsHome ? fixture.opponentTeamName : null;
  const awayTeamName = playerIsHome ? fixture.opponentTeamName : opponentIsHome ? fixture.playerTeamName : null;
  const title = fixture.label || `${homeTeamName || 'Home team'} v ${awayTeamName || 'Away team'}`;
  const playerMoments = playerMatchMoments(detail.events, detail.player.wyscoutId);
  const emphasizedEventIds = new Set(playerMoments.map(moment => moment.event.id));
  const eventRelationshipLabels = new Map(playerMoments.map(moment => [moment.event.id, moment.relationshipLabel] as const));
  const spatialAnalysis = detail.eventStatus === 'unavailable' ? undefined : (
    <WyscoutEventsAnalysis
      events={detail.events}
      matchId={detail.match.matchId}
      canWatchVideo={false}
      requestEventVideo={() => Promise.reject(new Error('Video is unavailable in this view.'))}
      homeTeamId={detail.match.homeTeamId}
      awayTeamId={detail.match.awayTeamId}
      homeTeamName={homeTeamName || 'Home team'}
      awayTeamName={awayTeamName || 'Away team'}
      emphasizedEventIds={emphasizedEventIds}
      eventRelationshipLabels={eventRelationshipLabels}
    />
  );

  return (
    <DetailPageFrame
      backHref={backHref}
      backLabel="Back to player"
      identity={<><p className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Wyscout match</p><h1 className="text-2xl font-bold text-gray-950 dark:text-white">{title}</h1></>}
    >
      {refreshAnnouncement && <p role="status" aria-live="polite" className="sr-only">{refreshAnnouncement}</p>}
      {staleError && (
        <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          {staleError} Cached match details remain visible. <button type="button" className="font-semibold underline" onClick={() => { void query.refetch(); }}>Retry</button>
        </p>
      )}
      <WyscoutPlayerMatchContent detail={detail} showHeading={false} allEventsAnalysis={spatialAnalysis} onRetry={() => { void query.refetch(); }} />
    </DetailPageFrame>
  );
};

export default WyscoutPlayerMatchPage;
